import { Container, Group, Loader, Table, Text } from '@mantine/core'
import { IconCircleFilled } from '@tabler/icons-react'
import { useMemo } from 'react'

import {
  EnrollmentStatusPerson,
  enrollmentStatusConfiguration,
} from '../enrollmentConfig'
import { PersonEnrollmentChecker } from '../types'
import { getEnrollmentCheckerDocument } from '../utils/getEnrollmentCheckerDocument'
import { verifyPersonEnrollmentStatus } from '../utils/verifyPersonEnrollmentStatus'

type Props = {
  data: PersonEnrollmentChecker
  isLoading: boolean
  isError: boolean
  isUninitialized: boolean
  searchTerm: string
}

const NOT_AVAILABLE_MESSAGE = 'TLC/DL Not Available.'

const EnrollmentResult = (searchEnrollmentCheckerResults: Props) => {
  const { data, isUninitialized, isLoading, searchTerm } =
    searchEnrollmentCheckerResults

  if (isUninitialized) {
    return null
  }

  if (isLoading) {
    return (
      <Group style={{ marginTop: 64 }} justify="center" align="center">
        <Loader color="blue" />
        <Text c="blue">Checking driver status…</Text>
      </Group>
    )
  }

  if (!data.isFound) {
    return (
      <Text
        style={{ marginTop: 64 }}
        ta="center"
        c="var(--mantine-color-red-6)"
      >
        Driver is not enrolled in Drivers Benefits.
      </Text>
    )
  }

  const personEnrollmentConfig = useMemo(() => {
    if (data.isFound && !isUninitialized) {
      const enrollmentStatus = verifyPersonEnrollmentStatus(data.enrollment)
      return enrollmentStatusConfiguration[enrollmentStatus]
    }
  }, [data.enrollment, data.isFound, isUninitialized])

  return (
    <Container style={{ marginTop: 48 }} w="450">
      <Table
        withColumnBorders={false}
        withRowBorders={false}
        withTableBorder={false}
        variant="vertical"
        layout="fixed"
      >
        <Table.Tbody>
          <Table.Tr>
            <Table.Th w={160}>Name</Table.Th>
            <Table.Td>{`${data.firstName} ${data.lastName}`}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>TLC/DL</Table.Th>
            <Table.Td>
              {getEnrollmentCheckerDocument({
                documents: data.documents!,
                searchTerm,
              }) || NOT_AVAILABLE_MESSAGE}
            </Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Mobile Phone</Table.Th>
            <Table.Td>{data.phoneNumber}</Table.Td>
          </Table.Tr>

          <Table.Tr>
            <Table.Th>Status</Table.Th>
            <Table.Td>
              <Group
                c={personEnrollmentConfig?.color}
                style={{
                  border: '1px solid',
                  borderRadius: 4,
                  background: personEnrollmentConfig?.border,
                }}
                align="center"
                justify="center"
              >
                <IconCircleFilled width="10" />
                {personEnrollmentConfig?.message}
              </Group>
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
      {personEnrollmentConfig?.type === EnrollmentStatusPerson.NOT_ENROLLED && (
        <Text mt="xl" ta="center" c={personEnrollmentConfig.color}>
          Cannot Use Benefits.
        </Text>
      )}
    </Container>
  )
}

export default EnrollmentResult
